import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import { Download, Image } from 'lucide-react';

const ResearchDesignDiagram = ({ dataA, dataB, wizardAComplete, wizardBComplete }) => {
  const diagramRef = useRef(null);

  if (!wizardAComplete || !dataA) {
    return null;
  }

  const getParadigmLabel = (value) => {
    const labels = {
      positivism: 'Positivism',
      interpretivism: 'Interpretivism',
      pragmatism: 'Pragmatism',
      critical_realism: 'Critical Realism',
      transformative: 'Transformative'
    };
    return labels[value] || value || 'Not specified';
  };

  const getDimensionLabel = (dimension, value) => {
    const labels = {
      ontology: {
        realist: 'Realism',
        relativist: 'Relativism',
        pragmatic: 'Pragmatic'
      },
      epistemology: {
        objectivist: 'Objectivism',
        subjectivist: 'Subjectivism',
        critical: 'Critical',
        practical: 'Practical'
      },
      axiology: {
        value_free: 'Value-Free',
        value_laden: 'Value-Laden',
        value_driven: 'Value-Driven',
        pragmatic_values: 'Pragmatic'
      }
    };
    return labels[dimension]?.[value] || value || '—';
  };

  const getTheoryLabel = (value) => {
    const labels = {
      deductive: 'Deductive',
      inductive: 'Inductive',
      abductive: 'Abductive'
    };
    return labels[value] || value || 'Not specified';
  };

  const getMethodLabel = (value) => {
    const labels = {
      quantitative: 'Quantitative',
      qualitative: 'Qualitative',
      mixed_methods: 'Mixed Methods'
    };
    return labels[value] || value || 'Not specified';
  };

  const getStrategyLabel = (value) => {
    const labels = {
      experiment: 'Experiment',
      survey: 'Survey',
      case_study: 'Case Study',
      action_research: 'Action Research',
      grounded_theory: 'Grounded Theory',
      ethnography: 'Ethnography',
      archival_research: 'Archival Research',
      narrative: 'Narrative Inquiry'
    };
    return labels[value] || value || 'Not specified';
  };

  const getTimeLabel = (value) => {
    const labels = {
      cross_sectional: 'Cross-Sectional',
      longitudinal: 'Longitudinal'
    };
    return labels[value] || value || 'Not specified';
  };

  const getSamplingLabel = (value) => {
    const labels = {
      simpleRandom: 'Simple Random',
      systematic: 'Systematic',
      stratified: 'Stratified',
      cluster: 'Cluster',
      multistage: 'Multistage',
      pps: 'PPS',
      convenience: 'Convenience',
      purposive: 'Purposive',
      quota: 'Quota',
      snowball: 'Snowball',
      volunteer: 'Volunteer',
      expert: 'Expert',
      theoretical: 'Theoretical',
      homogeneous: 'Homogeneous',
      maximumVariation: 'Maximum Variation',
      criticalCase: 'Critical Case',
      sequential: 'Sequential',
      adaptive: 'Adaptive',
      rds: 'RDS',
      panel: 'Panel'
    };
    return labels[value] || value;
  };

  const getSamplingDisplay = () => {
    const sampling = dataA.techniques?.sampling;
    if (!sampling) return '—';
    if (Array.isArray(sampling) && sampling.length > 0) {
      return sampling.map(s => getSamplingLabel(s)).join(', ');
    }
    return getSamplingLabel(sampling);
  };

  const getSampleSizeDisplay = () => {
    const sampleSizes = dataA.techniques?.sampleSizesPerMethod;
    if (sampleSizes && Object.keys(sampleSizes).length > 0) {
      const sizes = Object.values(sampleSizes).filter(s => s && parseInt(s) > 0);
      if (sizes.length === 1) {
        return `N = ${sizes[0]}`;
      } else if (sizes.length > 1) {
        const total = sizes.reduce((sum, s) => sum + parseInt(s), 0);
        return `N = ${total} (combined from all data sources)`;
      }
    }
    if (dataA.techniques?.samplingDetails?.targetSampleSize) {
      return `N = ${dataA.techniques.samplingDetails.targetSampleSize}`;
    }
    return '—';
  };

  const getDataCollectionMethods = () => {
    if (dataA.techniques?.dataCollection?.length > 0) {
      return dataA.techniques.dataCollection.slice(0, 3).join(', ');
    }
    return 'Not specified';
  };

  const handleDownloadPNG = async () => {
    if (!diagramRef.current) return;

    try {
      const canvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });

      const link = document.createElement('a');
      link.download = 'research_design_framework.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating PNG:', error);
      alert('Error generating image. Please try again.');
    }
  };

  return (
    <div className="research-diagram-container">
      <div className="diagram-header">
        <h3><Image size={20} /> Research Design Framework</h3>
        <button className="btn btn-primary" onClick={handleDownloadPNG}>
          <Download size={18} />
          Download as PNG
        </button>
      </div>

      <div className="diagram-wrapper" ref={diagramRef}>
        <div className="research-design-diagram">
          <div className="diagram-title">
            <h2>Research Design Framework</h2>
            <p className="diagram-subtitle">Based on Saunders' Research Onion</p>
          </div>

          <div className="diagram-layers">
            <div className="layer layer-philosophy">
              <div className="layer-label">Philosophy</div>
              <div className="layer-content">
                <div className="philosophy-box">
                  <div className="paradigm-main">{getParadigmLabel(dataA.philosophy)}</div>
                  <div className="dimensions-row">
                    <span className="dim-item">
                      <strong>Ontology:</strong> {getDimensionLabel('ontology', dataA.philosophicalDimensions?.ontology)}
                    </span>
                    <span className="dim-item">
                      <strong>Epistemology:</strong> {getDimensionLabel('epistemology', dataA.philosophicalDimensions?.epistemology)}
                    </span>
                    <span className="dim-item">
                      <strong>Axiology:</strong> {getDimensionLabel('axiology', dataA.philosophicalDimensions?.axiology)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="connector-arrow">▼</div>

            <div className="layer layer-approach">
              <div className="layer-label">Approach to Theory</div>
              <div className="layer-content">
                <div className="approach-box">{getTheoryLabel(dataA.theoryApproach)}</div>
              </div>
            </div>

            <div className="connector-arrow">▼</div>

            <div className="layer layer-method">
              <div className="layer-label">Methodological Choice</div>
              <div className="layer-content">
                <div className="method-box">{getMethodLabel(dataA.methodologicalChoice)}</div>
              </div>
            </div>

            <div className="connector-arrow">▼</div>

            <div className="layer layer-strategy">
              <div className="layer-label">Research Strategy</div>
              <div className="layer-content">
                <div className="strategy-box">{getStrategyLabel(dataA.strategy)}</div>
              </div>
            </div>

            <div className="connector-arrow">▼</div>

            <div className="layer layer-time">
              <div className="layer-label">Time Horizon</div>
              <div className="layer-content">
                <div className="time-box">{getTimeLabel(dataA.timeHorizon)}</div>
              </div>
            </div>

            <div className="connector-arrow">▼</div>

            <div className="layer layer-techniques">
              <div className="layer-label">Techniques & Procedures</div>
              <div className="layer-content">
                <div className="techniques-grid">
                  <div className="technique-item">
                    <span className="technique-label">Data Collection:</span>
                    <span className="technique-value">{getDataCollectionMethods()}</span>
                  </div>
                  <div className="technique-item">
                    <span className="technique-label">Sampling:</span>
                    <span className="technique-value">{getSamplingDisplay()}</span>
                  </div>
                  <div className="technique-item">
                    <span className="technique-label">Sample Size:</span>
                    <span className="technique-value">{getSampleSizeDisplay()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {wizardBComplete && dataB && (
            <>
              <div className="connector-arrow stat-connector">▼</div>
              <div className="statistical-section">
                <div className="stat-header">Statistical Analysis Plan</div>
                <div className="stat-content">
                  <div className="stat-grid">
                    <div className="stat-item">
                      <span className="stat-label">Research Question:</span>
                      <span className="stat-value">
                        {Array.isArray(dataB.questionFamilies) 
                          ? dataB.questionFamilies.join(', ') 
                          : dataB.questionFamily || '—'}
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">DV Type:</span>
                      <span className="stat-value">{dataB.variables?.dvType || '—'}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">IV Type:</span>
                      <span className="stat-value">
                        {dataB.variables?.ivTypes?.join(', ') || dataB.variables?.ivType || '—'}
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Sample Size:</span>
                      <span className="stat-value">N = {dataB.variables?.sampleSize || '—'}</span>
                    </div>
                    <div className="stat-item highlight">
                      <span className="stat-label">Primary Test:</span>
                      <span className="stat-value">{dataB.recommendations?.primaryTest || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="diagram-footer">
            <p>Generated by Research Methodology Wizard • {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchDesignDiagram;
